import { memo, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { usePermissionsStore } from "@/stores/permissions";
import { useAuthStore } from "@/stores/auth";
import { isSupplierRole } from "@/lib/workspaceAccess";
import {
  checkPermissions,
  checkSubscriptionPermission,
  isSponsoredRole,
} from "@/lib/permissions";

interface RbacRouteProps {
  /** One or more RBAC permission codes required to view the route. */
  permission?: string | string[];
  /** Plan feature code (e.g. "EMISSIONS", "SRM") required by the subscription. */
  planPermission?: string;
  /** Redirect target when the user is not permitted. */
  fallbackPath?: string;
  children: ReactNode;
}

/**
 * Route guard combining RBAC permissions and subscription plan gating.
 * Access stays closed until each required permission source has loaded.
 * This prevents an empty response caused by a failed request from being
 * interpreted as unrestricted access.
 */
export const RbacRoute = memo(function RbacRoute({
  permission,
  planPermission,
  fallbackPath,
  children,
}: RbacRouteProps) {
  const role = useAuthStore((s) => s.user?.role);
  const permissions = usePermissionsStore((s) => s.permissions);
  const subscriptionPermissions = usePermissionsStore(
    (s) => s.subscriptionPermissions,
  );
  const addonPermissions = usePermissionsStore((s) => s.addonPermissions);
  const loading = usePermissionsStore((s) => s.loading);
  const loaded = usePermissionsStore((s) => s.loaded);
  const permissionsLoaded = usePermissionsStore((s) => s.permissionsLoaded);
  const subscriptionLoaded = usePermissionsStore((s) => s.subscriptionLoaded);

  if (loading && !loaded) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <div
          className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"
          role="status"
          aria-label="Checking access"
        />
      </div>
    );
  }

  const required = Array.isArray(permission)
    ? permission
    : permission
      ? [permission]
      : [];

  const lacksPermission =
    required.length > 0 &&
    (!permissionsLoaded || !checkPermissions(required, permissions));

  const lacksPlan =
    !isSponsoredRole(role) &&
    !!planPermission &&
    (!subscriptionLoaded ||
      !checkSubscriptionPermission(
        planPermission,
        subscriptionPermissions,
        -1,
        addonPermissions,
      ));

  if (lacksPermission || lacksPlan) {
    return (
      <Navigate
        to={
          fallbackPath ?? (isSupplierRole(role) ? "/resourcecentre" : "/home")
        }
        replace
      />
    );
  }

  return <>{children}</>;
});
